import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Info, BarChart3, Target, TrendingUp } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { supabase } from '@/integrations/supabase/client';
import { explanationText } from '@/utils/matches/analysisText';

interface MatchCircularOverviewProps {
  match: any;
}

interface CategoryData {
  name: string;
  shortName: string;
  score: number;
  color: string;
  lightColor: string;
  details: {
    explanation: string;
    pairScores: { [key: string]: number };
    matchRatio: number;
  };
}

// Function to get color based on score (0-10 scale)
const getScoreColor = (score: number): { color: string; lightColor: string } => {
  if (score >= 8) {
    return { color: '#22C55E', lightColor: '#DCFCE7' }; // Green - Excellent match
  } else if (score >= 6) {
    return { color: '#84CC16', lightColor: '#ECFCCB' }; // Light green - Good match
  } else if (score >= 4) {
    return { color: '#EAB308', lightColor: '#FEF3C7' }; // Yellow - Average match
  } else if (score >= 2) {
    return { color: '#F97316', lightColor: '#FED7AA' }; // Orange - Poor match
  } else {
    return { color: '#EF4444', lightColor: '#FEE2E2' }; // Red - Very poor match
  }
};

type ViewMode = 'bars' | 'rings';

export const MatchCircularOverview: React.FC<MatchCircularOverviewProps> = ({ match }) => {
  const { t } = useTranslation();
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('rings');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  // Load user preference on component mount
  useEffect(() => {
    const loadUserPreference = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('settings')
            .eq('id', user.id)
            .single();
          
          if (profile?.settings?.matchViewMode) {
            setViewMode(profile.settings.matchViewMode);
          }
        }
      } catch (error) {
        console.error('Error loading user preference:', error);
      }
    };

    loadUserPreference();
  }, []);

  // Save user preference when view mode changes
  const handleViewModeChange = async (newViewMode: ViewMode) => {
    setViewMode(newViewMode);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('settings')
          .eq('id', user.id)
          .single();
        
        const currentSettings = profile?.settings || {};
        const updatedSettings = {
          ...currentSettings,
          matchViewMode: newViewMode
        };
        
        await supabase
          .from('profiles')
          .update({ settings: updatedSettings })
          .eq('id', user.id);
      }
    } catch (error) {
      console.error('Error saving user preference:', error);
    }
  };

  if (!match?.ai_analysis?.analysis?.matching_analysis) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <Star className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <p>{t('matching.noAnalysisData', 'No analysis data available')}</p>
        </div>
      </div>
    );
  }

  const analysisData = match.ai_analysis.analysis.matching_analysis;

  // Helper function to create short names from category names
  const createShortName = (categoryName: string): string => {
    // Handle common patterns in category names
    const shortNameMap: { [key: string]: string } = {
      'Management & Oversight': 'MANAGEMENT',
      'Workload & Expectations': 'WORKLOAD',
      'Hard Skills & Experience': 'SKILLS',
      'Innovation & Adaptability': 'INNOVATION',
      'Remote Work & Flexibility': 'REMOTE WORK',
      'Compensation & Career Growth': 'COMPENSATION',
      'Work Environment & Culture Fit': 'CULTURE FIT'
    };

    if (shortNameMap[categoryName]) {
      return shortNameMap[categoryName];
    }

    // Fallback: create short name from category name
    return categoryName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .toUpperCase();
  };

  // Dynamically generate categories from AI analysis data
  const categories: CategoryData[] = Object.entries(analysisData).map(([categoryName, categoryData]: [string, any]) => {
    const matchRatio = categoryData?.['Match Ratio'] || 0;
    const score = Math.round(matchRatio * 10);
    
    return {
      name: categoryName,
      shortName: createShortName(categoryName),
      score: score,
      ...getScoreColor(score),
      details: {
        explanation: explanationText(categoryData?.['Explanation']),
        pairScores: categoryData?.['Pair score'] || {},
        matchRatio: matchRatio
      }
    };
  });


  return (
    <div className="w-full">
      {/* Header with view toggle */}
      <div className="flex justify-between items-start mb-8">
        <div className="flex-1">
          <div className="text-sm text-gray-600 dark:text-gray-300 w-full leading-relaxed mb-6">
            {match.ai_analysis?.analysis?.final_recommendation || 
              `${match.candidates?.name} demonstrates exceptional alignment with the ${match.jobs?.title} position. Our advanced AI analysis reveals outstanding compatibility across multiple dimensions, indicating strong potential for success in this role.`
            }
          </div>
        </div>
        
        {/* View Mode Toggle - Top Right */}
        <div className="flex bg-gray-100 rounded-sm p-1">
          <Button
            variant={viewMode === 'bars' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleViewModeChange('bars')}
            className="flex items-center gap-2"
          >
            <BarChart3 className="h-4 w-4" />
            Bars
          </Button>
          <Button
            variant={viewMode === 'rings' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleViewModeChange('rings')}
            className="flex items-center gap-2"
          >
            <Target className="h-4 w-4" />
            Rings
          </Button>
        </div>
      </div>

      {/* Key Insights from Overall Analysis */}
      {(match.ai_analysis?.analysis?.key_strengths || match.ai_analysis?.analysis?.key_gaps) && (
        <div className="mb-8 grid md:grid-cols-2 gap-6">
          {/* Key Strengths */}
          {match.ai_analysis?.analysis?.key_strengths && match.ai_analysis.analysis.key_strengths.length > 0 && (
            <div className="bg-green-50/60 backdrop-blur-md rounded-sm p-6 shadow-sm border border-green-100">
              <h4 className="font-semibold text-green-700 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                {t('matching.sections.keyStrengths', 'Key Strengths')}
              </h4>
              <ul className="space-y-2">
                {match.ai_analysis.analysis.key_strengths.map((strength: string, index: number) => (
                  <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Key Gaps */}
          {match.ai_analysis?.analysis?.key_gaps && match.ai_analysis.analysis.key_gaps.length > 0 && (
            <div className="bg-red-50/60 backdrop-blur-md rounded-sm p-6 shadow-sm border border-red-100">
              <h4 className="font-semibold text-red-700 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                {t('matching.sections.areasForImprovement', 'Key Gaps')}
              </h4>
              <ul className="space-y-2">
                {match.ai_analysis.analysis.key_gaps.map((gap: string, index: number) => (
                  <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{gap}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}


      {/* Chart Container */}
      <div className="flex justify-center mb-8">

        {viewMode === 'bars' && (
          <div className="w-full grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {categories.map((category, index) => (
              <div 
                key={index}
                className="cursor-pointer transition-all hover:scale-[1.02]"
                onMouseEnter={() => setHoveredCategory(index)}
                onMouseLeave={() => setHoveredCategory(null)}
                onClick={() => setSelectedCategory(selectedCategory === index ? null : index)}
              >
                    <div className="flex items-center gap-3 p-4 bg-white rounded-sm shadow-sm border hover:shadow-md transition-shadow">
                      <div className="flex-shrink-0 w-20 text-xs font-medium text-gray-700 leading-tight">
                        {category.name}
                      </div>
                      <div className="flex-1 relative">
                        <div className="h-7 bg-gray-200 rounded-sm overflow-hidden">
                          <div 
                            className="h-full transition-all duration-500 flex items-center justify-end pr-2 rounded-sm"
                            style={{ 
                              width: `${(category.score / 10) * 100}%`,
                              backgroundColor: category.color
                            }}
                          >
                            <span className="text-white font-bold text-xs">
                              {category.score}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
            ))}
          </div>
        )}

        {viewMode === 'rings' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6 w-full">
            {categories.map((category, index) => (
              <div 
                key={index}
                className="flex flex-col items-center cursor-pointer transition-all hover:scale-105"
                onMouseEnter={() => setHoveredCategory(index)}
                onMouseLeave={() => setHoveredCategory(null)}
                onClick={() => setSelectedCategory(selectedCategory === index ? null : index)}
              >
                    <div className="relative mb-4">
                      <svg width="120" height="120" className="transform -rotate-90">
                        <circle
                          cx="60"
                          cy="60"
                          r="45"
                          fill="none"
                          stroke="#E5E7EB"
                          strokeWidth="8"
                        />
                        <circle
                          cx="60"
                          cy="60"
                          r="45"
                          fill="none"
                          stroke={category.color}
                          strokeWidth="8"
                          strokeDasharray={`${(category.score / 10) * 283} 283`}
                          strokeLinecap="round"
                          className="transition-all duration-500"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                          {category.score}
                        </span>
                      </div>
                    </div>
                    <div className="text-center">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">
                        {category.name}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {category.details.explanation?.substring(0, 50)}...
                      </p>
                    </div>
                  </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Category Details Accordion */}
      {selectedCategory !== null && (
        <div className="mt-8 w-full animate-in slide-in-from-bottom-4 duration-500">
          <Accordion type="single" collapsible defaultValue="details" className="w-full">
            <AccordionItem value="details" className="border border-gray-200 rounded-sm shadow-lg bg-white">
              <AccordionTrigger className="px-6 py-4 hover:no-underline bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-sm">
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded-sm shadow-sm"
                    style={{ backgroundColor: categories[selectedCategory].color }}
                  />
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-xl">{categories[selectedCategory].name}</h4>
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Score:</span>
                    <span 
                      className="text-lg font-bold px-2 py-1 rounded-sm text-white"
                      style={{ backgroundColor: categories[selectedCategory].color }}
                    >
                      {categories[selectedCategory].score}/10
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory(null);
                      }}
                      className="ml-2 p-1 hover:bg-gray-200 rounded-sm transition-colors"
                    >
                      <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 animate-in slide-in-from-top-2 duration-300">
                <div className="space-y-6 pt-4">
                  {/* Overall Match Section */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 rounded-sm border">
                    <div className="text-sm text-gray-600 mb-3">
                      <strong className="text-gray-800 text-base">Overall Match:</strong>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 font-medium">Low</span>
                      <div className="flex-1 bg-gray-200 rounded-sm h-3 shadow-inner">
                        <div 
                          className="h-3 rounded-sm transition-all duration-700 ease-out shadow-sm"
                          style={{ 
                            width: `${Math.round(categories[selectedCategory].details.matchRatio * 100)}%`,
                            backgroundColor: categories[selectedCategory].color
                          }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">High</span>
                    </div>
                    <div className="text-right mt-2">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {Math.round(categories[selectedCategory].details.matchRatio * 100)}% Match
                      </span>
                    </div>
                  </div>
                  
                  {/* Explanation Section */}
                  {categories[selectedCategory].details.explanation && (
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                      <h5 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        Analysis
                      </h5>
                      <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                        {categories[selectedCategory].details.explanation}
                      </div>
                    </div>
                  )}
                  
                  {/* Detailed Scores Section */}
                  {Object.keys(categories[selectedCategory].details.pairScores).length > 0 && (
                    <div className="space-y-4">
                      <h5 className="font-semibold text-gray-900 dark:text-gray-100 text-base flex items-center gap-2">
                        <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                        Detailed Breakdown
                      </h5>
                      <div className="grid gap-3">
                        {Object.entries(categories[selectedCategory].details.pairScores).map(([key, value], idx) => {
                          const numericValue = typeof value === 'number' ? value : 0;
                          const percentage = Math.round(numericValue * 100);
                          const gaugeColor = getScoreColor(Math.round(numericValue * 10)).color;
                          
                          return (
                            <div 
                              key={key} 
                              className="bg-white border border-gray-200 rounded-sm p-4 hover:shadow-md transition-all duration-200"
                              style={{ animationDelay: `${idx * 100}ms` }}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-sm font-medium text-gray-800 dark:text-gray-200" style={{ lineHeight: '1.4' }}>
                                  {key.replace(/_/g, ' ').replace(/vs/g, ' → ').replace(/\b\w/g, l => l.toUpperCase())}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">{percentage}%</span>
                                  <div 
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: gaugeColor }}
                                  ></div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400 font-medium">Low</span>
                                <div className="flex-1 bg-gray-200 rounded-sm h-2">
                                  <div 
                                    className="h-2 rounded-sm transition-all duration-500 ease-out"
                                    style={{ 
                                      width: `${percentage}%`,
                                      backgroundColor: gaugeColor
                                    }}
                                  />
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">High</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}

      {/* Color Scale Legend */}
      <div className="mb-6 p-4 bg-gray-50 rounded-sm">
        <div className="text-sm font-medium text-gray-700 mb-3 text-center">Match Quality Scale</div>
        <div className="flex justify-center items-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-gray-600 dark:text-gray-400">0-1 Poor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span className="text-gray-600 dark:text-gray-400">2-3 Below Avg</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-gray-600 dark:text-gray-400">4-5 Average</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-lime-500"></div>
            <span className="text-gray-600 dark:text-gray-400">6-7 Good</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-gray-600 dark:text-gray-400">8-10 Excellent</span>
          </div>
        </div>
      </div>

      {/* Clean Legend Grid - Commented out as it's redundant with the main chart views */}
    </div>
  );
};